import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import RouteService from '~/modules/services/route-service';

const SHORTCUTS = [
	{
		group: 'Navigation',
		items: [
			{ keys: ['⌘', 'K'], description: 'Open command palette' },
			{ keys: ['?'], description: 'Show keyboard shortcuts' }
		]
	},
	{
		group: 'Projects',
		state: 'project',
		items: [
			{ keys: ['/'], description: 'Focus search' },
			{ keys: ['Tab'], description: 'Focus first card' },
			{ keys: ['↑', '↓', '←', '→'], description: 'Navigate cards' },
			{ keys: ['↵'], description: 'Open asset' },
			{ keys: ['⇧', 'A'], description: 'Select all visible assets' },
			{ keys: ['⇧', 'Q'], description: 'Add selected to order queue' }
		]
	},
	{
		group: 'Asset',
		state: 'asset',
		items: [
			{ keys: ['Space'], description: 'Play / pause video' },
			{ keys: ['F'], description: 'Toggle fullscreen' },
			{ keys: ['⇧', 'S'], description: 'Add to share queue' },
			{ keys: ['⇧', 'O'], description: 'Order asset & go to queue' }
		]
	}
];

const currentState = () => RouteService.currentState?.name ?? '';

const Grid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 2rem;

	@media (max-width: 540px) {
		grid-template-columns: 1fr;
	}
`;

const Group = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
`;

const GroupHeading = styled.p`
	margin: 0 0 0.5rem;
	font-size: 0.6875rem;
	font-weight: 500;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: hsl(var(--muted-foreground));
`;

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	padding: 0.375rem 0;
	border-bottom: 1px solid hsl(var(--border) / 0.4);

	&:last-child {
		border-bottom: none;
	}
`;

const Description = styled.span`
	font-size: 0.8125rem;
	color: hsl(var(--foreground));
`;

const Keys = styled.div`
	display: flex;
	align-items: center;
	gap: 0.25rem;
	flex-shrink: 0;
`;

const Kbd = styled.kbd`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 1.5rem;
	height: 1.5rem;
	padding: 0 0.375rem;
	font-family: inherit;
	font-size: 0.75rem;
	font-weight: 500;
	color: hsl(var(--foreground));
	background: hsl(var(--midnight-300));
	border: 1px solid hsl(var(--border));
	border-bottom-width: 2px;
	border-radius: 4px;
	line-height: 1;
`;

export default function KeyboardShortcutsDialog({ open, onOpenChange }) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="rounded-xl" style={{ maxWidth: '36rem' }}>
				<DialogHeader className="pb-4">
					<DialogTitle>Keyboard Shortcuts</DialogTitle>
				</DialogHeader>
				<div className="px-8 pb-7">
					<Grid>
						{SHORTCUTS.filter((s) => !s.state || s.state === currentState()).map((section) => (
							<Group key={section.group}>
								<GroupHeading>{section.group}</GroupHeading>
								{section.items.map((item) => (
									<Row key={item.description}>
										<Description>{item.description}</Description>
										<Keys>
											{item.keys.map((key) => (
												<Kbd key={key}>{key}</Kbd>
											))}
										</Keys>
									</Row>
								))}
							</Group>
						))}
					</Grid>
				</div>
			</DialogContent>
		</Dialog>
	);
}

KeyboardShortcutsDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onOpenChange: PropTypes.func.isRequired
};
